import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Typography,
  Button,
  Modal,
  IconButton,
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import { useDispatch } from "react-redux";
import { updateElement } from "../../../store/formSlice";
import CloseIcon from "@mui/icons-material/Close";

const Signature = (props) => {
  const dispatch = useDispatch();
  const [trimmedDataURL, setTrimmedDataURL] = useState(
    props.signatureMode === "Scribble" ? props.value : null
  );
  const [signatureMode, setSignatureMode] = useState(
    props.signatureMode || "Scribble"
  );
  const [typedSignature, setTypedSignature] = useState(
    props.signatureMode === "Type" ? props.value : null
  );
  const sigPadRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const clear = () => {
    if (signatureMode === "Scribble") {
      sigPadRef.current.clear();
      setTrimmedDataURL(null);
    } else {
      setTypedSignature("");
    }
    const updatedElement = {
      id: props.elementId,
      element: {
        ...props,
      },
    };
    dispatch(updateElement(updatedElement));
  };

  const handleSignatureChange = () => {
    let signature;
    if (signatureMode === "Scribble" && !sigPadRef.current.isEmpty()) {
      signature = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png");
      setTrimmedDataURL(signature);
      setSignatureMode("Scribble");
    } else if (signatureMode === "Type" && typedSignature) {
      signature = typedSignature;
      setSignatureMode("Type");
    }

    if (signature) {
      //setTrimmedDataURL(signature);
      // Update the element in the form state with the signature data
      const updatedElement = {
        id: props.elementId,
        element: {
          ...props,
          signatureMode: signatureMode,
          value: signature,
        },
      };
      dispatch(updateElement(updatedElement));
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
    if (props.signatureMode === "Type" || signatureMode === "Type") {
      setSignatureMode("Type");
    } else {
      setSignatureMode("Scribble");
      setTimeout(() => {
        if (sigPadRef.current && trimmedDataURL) {
          //sigPadRef.current.fromDataURL(trimmedDataURL);
          const ctx = sigPadRef.current.getCanvas().getContext("2d");
          const img = new Image();
          img.src = trimmedDataURL;
          img.onload = () => {
            const centerX =
              (sigPadRef.current.getCanvas().width - img.width) / 2;
            const centerY =
              (sigPadRef.current.getCanvas().height - img.height) / 2;
            ctx.drawImage(img, centerX, centerY);
          };
        }
      }, 100);
    }
  };
  const handleToggleChange = (event, newMode) => {
    if (newMode) {
      setSignatureMode(newMode);
    }
    if (newMode === "Scribble") {
      setTimeout(() => {
        if (sigPadRef.current && trimmedDataURL) {
          const ctx = sigPadRef.current.getCanvas().getContext("2d");
          const img = new Image();
          img.src = trimmedDataURL;
          img.onload = () => {
            const centerX =
              (sigPadRef.current.getCanvas().width - img.width) / 2;
            const centerY =
              (sigPadRef.current.getCanvas().height - img.height) / 2;
            ctx.drawImage(img, centerX, centerY);
          };
        }
      }, 100);
    }
  };

  return (
    <>
      <Button
        id="btnSignature"
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          width: { xs: "100%", sm: props.width || "75%" },
          marginLeft: props.marginLeft,
          marginTop: props.marginTop,
        }}
        disabled={props.disabled ?? false}
      >
        {hover && (trimmedDataURL || typedSignature) ? (
          "Edit Signature"
        ) : (
          <>
            {trimmedDataURL ? (
              <img
                src={trimmedDataURL}
                alt="Signature Preview"
                style={{
                  maxHeight: "24px",
                  maxWidth: "100%",
                  marginRight: "8px",
                }}
              />
            ) : typedSignature ? (
              <Typography variant="body2" sx={{ marginRight: "8px" }}>
                {typedSignature}
              </Typography>
            ) : (
              "Open Signature"
            )}
          </>
        )}
      </Button>

      {/* {trimmedDataURL && signatureMode === "Scribble" ? (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <img
            src={trimmedDataURL}
            alt="Signature Preview"
            style={{ border: "1px solid #ccc", maxWidth: "100%", maxHeight: "150px" }}
          />
        </Box>
      ) : signatureMode === "Type" && typedSignature ? (
        <Typography sx={{ mt: 2, textAlign: "center" }}>
          {typedSignature}
        </Typography>
      ) : null} */}

      <Modal
        open={open}
        onClose={handleClose}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Paper
          sx={{
            position: "relative",
            width: { xs: "100%", sm: "75%", md: "600px" },
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            sx={{ position: "absolute", top: 0, right: 0 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          {props.label && (
            <Typography variant="subtitle1" gutterBottom>
              {props.label}
            </Typography>
          )}
          <ToggleButtonGroup
            value={signatureMode}
            exclusive
            onChange={handleToggleChange}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="Type">Type</ToggleButton>
            <ToggleButton value="Scribble">Draw</ToggleButton>
          </ToggleButtonGroup>
          {signatureMode === "Type" ? (
            <TextField
              fullWidth
              label="Type Your Signature"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
            />
          ) : (
            <SignatureCanvas
              ref={sigPadRef}
              penColor="#2C7BB9"
              canvasProps={{
                width: isMobile ? 300 : 600,
                height: isMobile ? 200 : 400,
                className: "sigCanvas",
                style: {
                  border: "1px dashed  #000", // black border
                  borderRadius: "8px", // optional rounded corners
                },
              }}
            />
          )}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={clear} sx={{ mr: 2 }}>
              Clear
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSignatureChange}
            >
              OK
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
};

Signature.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  elementId: PropTypes.string.isRequired,
  label: PropTypes.string,
  marginLeft: PropTypes.string,
  marginTop: PropTypes.string,
  disabled: PropTypes.bool,
};

export default Signature;
